import { createSlice } from '@reduxjs/toolkit';
// routes
import { PATH_SERVICE_PRO } from '../../routes/paths';
import webServices from '../../services/webServices';

// ----------------------------------------------------------------------

const initialState = {
  isLoading: false,
  error: false,
  success: '',
  customerList: []
};

const slice = createSlice({
  name: 'customer',
  initialState,
  reducers: {
    startLoading(state) {
      state.isLoading = true;
    },
    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },
    hasSuccess(state, action) {
      state.isLoading = false;
      state.success = action.payload;
    },
    getCustomersSuccess(state, action) {
      state.isLoading = false;
      state.customerList = action.payload;
      state.success = '';
    }
  }
});

// Reducer
export default slice.reducer;

// Actions
export const { getCustomersSuccess } = slice.actions;
const { hasSuccess } = slice.actions;

// Resolve the logged-in user's own company id. Customers are scoped by companyId;
// use the company already loaded into state (getCompany is scoped by the caller's
// UID on the backend), falling back to a fetch if the company page hasn't been
// visited yet this session.
async function resolveCompanyId(getState) {
  const list = getState().company?.List;
  let company = Array.isArray(list) ? list[0] : null;
  if (!company) {
    const resp = await webServices.getCompany({ params: {} });
    if (resp && resp.success === true && Array.isArray(resp.data)) {
      [company] = resp.data;
    }
  }
  return company?.id;
}

export function getCustomers() {
  return async (dispatch, getState) => {
    dispatch(slice.actions.startLoading());
    try {
      const companyId = await resolveCompanyId(getState);
      if (!companyId) {
        dispatch(getCustomersSuccess([]));
        return;
      }
      const resp = await webServices.getCustomers(companyId);
      if (resp && resp.success === true) {
        dispatch(getCustomersSuccess(resp.data));
      }
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function createCustomer(data, navigate) {
  return async (dispatch, getState) => {
    dispatch(slice.actions.startLoading());
    try {
      const companyId = await resolveCompanyId(getState);
      if (!companyId) {
        dispatch(slice.actions.hasError());
        return;
      }
      const resp = await webServices.createCustomer({ ...data, companyId });
      if (resp && resp.success === true) {
        dispatch(hasSuccess('Create success'));
        navigate(PATH_SERVICE_PRO.customers);
      } else {
        dispatch(slice.actions.hasError());
      }
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function modifyCustomer(data, navigate, previousData) {
  return async (dispatch, getState) => {
    dispatch(slice.actions.startLoading());
    try {
      const companyId = await resolveCompanyId(getState);
      if (!companyId) {
        dispatch(slice.actions.hasError());
        return;
      }
      const { id } = previousData;
      const resp = await webServices.updateCustomer(id, { ...data, companyId });
      if (resp && resp.success === true) {
        dispatch(hasSuccess('Modify success'));
        navigate(PATH_SERVICE_PRO.customers);
      } else {
        dispatch(slice.actions.hasError());
      }
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function importCustomers(rows) {
  return async (dispatch, getState) => {
    dispatch(slice.actions.startLoading());
    try {
      const companyId = await resolveCompanyId(getState);
      if (!companyId) {
        dispatch(slice.actions.hasError());
        return;
      }
      const resp = await webServices.bulkCreateCustomers({ companyId, customers: rows });
      if (resp && resp.success === true) {
        dispatch(hasSuccess(resp.message || 'Import success'));
      } else {
        dispatch(slice.actions.hasError());
      }
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function deleteCustomers(ids) {
  return async (dispatch, getState) => {
    try {
      const companyId = await resolveCompanyId(getState);
      if (!companyId) {
        dispatch(slice.actions.hasError());
        return;
      }
      const resp = await webServices.deleteCustomer({ ids, companyId });
      if (resp && resp.success === true) {
        dispatch(hasSuccess('Delete success'));
      }
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}
